import {addToast} from '@heroui/toast';
import {Icon} from '@iconify/react';
import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';

import services from '../services';
import {RouteList, SideAvatars} from '../tournaments/live/RoadToVictory';
import {Podium} from './ShareResultsScreen';
import {prefersReducedMotion, resultsHeading, roadHeading, runYourOwnHref} from './resultsModel';

/**
 * The public page of a finished event opens on this: the champion and the podium (a league's final
 * table places with their points), the road to victory, and the ways to pass it on. ScorePal-branded
 * on Free with "Run your own". Its view is counted once per event per day.
 */
export default function ResultsSection({eventUid}: {eventUid: string}) {
    const query = useQuery({
        queryKey: ['event-results', eventUid, 'view'],
        queryFn: () => services.results.get(eventUid, true),
        enabled: !!eventUid,
        staleTime: 60_000,
        retry: 1,
    });
    const reduced = useMemo(prefersReducedMotion, []);
    const r = query.data;
    if (!r) return null;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(r.public_url);
            addToast({color: 'success', title: 'Link copied'});
            services.results.shared(eventUid, 'copy').catch(() => undefined);
        } catch {
            addToast({color: 'warning', title: 'Could not copy the link.'});
        }
    };

    return (
        <section className="mt-5 overflow-hidden rounded-3xl border border-[#f5c518]/30 bg-gradient-to-br from-[#04102e] to-[#0c2c7a] p-5 sm:p-8" aria-label="Final results" data-testid="results-section">
            <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-start lg:gap-10">
                <div className="flex w-full flex-col items-center text-center lg:w-5/12">
                    <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#cddbff]">{resultsHeading(r.kind)}</p>
                    <Icon icon="mdi:trophy" className="mt-3 text-6xl text-[#f5c518] drop-shadow-lg" />
                    <div className="mt-3"><SideAvatars label={r.champion.label} photos={r.champion.photos} size={60} /></div>
                    <p className="mt-4 text-xs font-bold uppercase tracking-[0.4em] text-[#f5c518]">Champion</p>
                    <h2 className="mt-1 max-w-md break-words font-bebas text-4xl font-bold tracking-wide text-white sm:text-5xl">{r.champion.label}</h2>
                    {r.podium[0]?.metric && <p className="mt-1 text-sm font-semibold text-[#f5c518]">{r.podium[0].metric}</p>}
                    <Podium podium={r.podium} reduced delay={0} />

                    <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                        <a href={r.whatsapp_url} target="_blank" rel="noopener noreferrer" onClick={() => services.results.shared(eventUid, 'whatsapp').catch(() => undefined)}
                           className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-sm font-bold text-[#04102e] transition hover:brightness-110">
                            <Icon icon="mdi:whatsapp" className="text-lg" /> Share on WhatsApp
                        </a>
                        <button type="button" onClick={copyLink}
                                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/20">
                            <Icon icon="mdi:link-variant" className="text-lg" /> Copy link
                        </button>
                    </div>
                    {!r.event.branded && (
                        <a href={runYourOwnHref(r.event.sport_type)} className="mt-4 text-xs font-semibold text-[#cddbff] underline-offset-4 hover:underline">
                            Run your own {r.event.sport_label ? r.event.sport_label.toLowerCase() + ' ' : ''}event on ScorePal →
                        </a>
                    )}
                </div>

                {r.route.length > 0 && (
                    <div className="w-full lg:w-7/12">
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#cddbff]">{roadHeading(r.kind)} · {r.champion.label}</p>
                        <RouteList route={r.route} reduced={reduced} />
                    </div>
                )}
            </div>
        </section>
    );
}
